import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Modal, Text } from '@/components/ui';
import { useApi } from '@/hooks/use-api';
import { useGlobalSearch } from '@/hooks/use-global-search';
import { useRefreshLiveData } from '@/hooks/use-live-refresh';
import { usePermissions } from '@/hooks/use-permissions';

/**
 * The v2 top bar (#279) — page title, search, and the primary create action.
 *
 * ── THE SHORTCUT HINT ──
 *
 * `⌘K` / `Alt+K` already worked before this (`components/global-search-popup.tsx` binds
 * `key === 'k' && (metaKey || altKey)`); it was simply never shown anywhere, which is one
 * of the three things #279 originally asked for. The hint is the whole fix — the binding
 * needed no change.
 *
 * Mac shows ⌘K, everything else Alt+K, because that is what the binding actually accepts.
 * SBS runs Windows, so Alt+K is the label most people here will see.
 *
 * The binding is web-only, so native never shows the hint.
 *
 * ── WEB, AND NATIVE AT LARGE WIDTHS ──
 *
 * Narrow native has a real navigation header and the search lives in it, so a second bar
 * would be a duplicate. At large widths (iPad landscape) the drawer screens hide that header
 * (`headerShown: !isLargeScreen` in `app/index.tsx`), so native renders this bar there and
 * pads it below the status bar itself. `initials` avoids pulling in an avatar component for
 * what is two characters.
 */

const initialsOf = (name?: string | null): string => {
	const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return '?';
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const V2HeaderBar: React.FC<{ title?: string }> = ({ title = 'Dashboard' }) => {
	const { user } = useApi();
	const { openSearch } = useGlobalSearch();
	const { canCreateWorkOrders } = usePermissions();
	const { refresh, refreshing } = useRefreshLiveData();
	const insets = useSafeAreaInsets();

	const [createOpen, setCreateOpen] = useState(false);

	const isWeb = Platform.OS === 'web';
	// Matches the binding in global-search-popup.tsx: metaKey OR altKey.
	const shortcut = isWeb && typeof navigator !== 'undefined'
		&& /Mac|iPhone|iPad/.test(navigator.platform ?? '')
		? '⌘K'
		: 'Alt+K';

	return (
		<View style={[styles.bar, !isWeb && { paddingTop: 14 + insets.top }]} testID="v2-header-bar">
			<Text style={styles.title}>{title}</Text>

			<Pressable onPress={() => openSearch()} style={styles.search} testID="v2-header-search">
				<Ionicons name="search-outline" size={17} color="#90a4ae" />
				<Text style={styles.searchText}>Search...</Text>
				{/* The hint #279 asked for — the binding already existed. */}
				{isWeb && (
					<View style={styles.kbd}>
						<Text style={styles.kbdText}>{shortcut}</Text>
					</View>
				)}
			</Pressable>

			{/*
				REFRESH, next to the create action.

				The dashboard is the one screen people leave open all day, and on desktop it
				has no navigation header at all (`headerShown: !isLargeScreen` in
				`app/index.tsx`), so the RefreshButton that lives there was invisible to
				exactly the people who needed it. This one refreshes every badge as well as
				the tiles — see `LIVE_QUERY_KEYS`.
			*/}
			<Pressable
				onPress={() => void refresh()}
				disabled={refreshing}
				style={[styles.iconButton, refreshing && styles.iconButtonBusy]}
				testID="v2-header-refresh"
				accessibilityLabel="Refresh"
			>
				<Ionicons name="refresh" size={19} color={refreshing ? '#b0bec5' : '#546e7a'} />
			</Pressable>

			{/*
				A PLUS, not a labelled button (#279 follow-up).

				There are three things someone creates from here and they are not variations
				of one another — a recurring work order is a schedule, a quote is not a job
				yet. A button labelled with only the first of them made the other two
				undiscoverable, and a dropdown caret on a button whose label is already an
				action reads as "more of that thing" rather than "or these".
			*/}
			{canCreateWorkOrders && (
				<Pressable
					onPress={() => setCreateOpen(true)}
					style={styles.plus}
					testID="v2-header-create"
					accessibilityLabel="Create new"
				>
					<Ionicons name="add" size={22} color="#fff" />
				</Pressable>
			)}

			<Pressable
				onPress={() => router.push('/settings')}
				style={styles.avatar}
				testID="v2-header-avatar"
				accessibilityLabel={user?.name ?? 'Account'}
			>
				<Text style={styles.avatarText}>{initialsOf(user?.name)}</Text>
			</Pressable>

			<CreateModal visible={createOpen} onDismiss={() => setCreateOpen(false)} />
		</View>
	);
};

/** What the plus button offers. Each is a genuinely different thing to create. */
const CREATE_OPTIONS = [
	{
		label: 'Work Order',
		description: 'A one-off job at a property.',
		icon: 'construct-outline' as const,
		href: '/work-orders/new',
	},
	{
		label: 'Recurring Work Order',
		description: 'A job that repeats on a schedule.',
		icon: 'repeat-outline' as const,
		href: '/work-orders/new?recurring=1',
	},
	{
		label: 'Quote',
		description: 'A price for work not yet approved.',
		icon: 'pricetag-outline' as const,
		href: '/quotes/new',
	},
];

const CreateModal: React.FC<{ visible: boolean; onDismiss: () => void }> = ({ visible, onDismiss }) => (
	<Modal visible={visible} onDismiss={onDismiss} dismissOnBackdrop>
		<Text style={styles.modalTitle}>What would you like to create?</Text>
		{CREATE_OPTIONS.map((o) => (
			<Pressable
				key={o.href}
				onPress={() => { onDismiss(); router.push(o.href as never); }}
				style={styles.option}
				testID={`v2-create-${o.href}`}
			>
				<View style={styles.optionIcon}>
					<Ionicons name={o.icon} size={22} color="#1976d2" />
				</View>
				<View style={styles.optionText}>
					<Text style={styles.optionLabel}>{o.label}</Text>
					<Text style={styles.optionDesc}>{o.description}</Text>
				</View>
				<Ionicons name="chevron-forward" size={18} color="#b0bec5" />
			</Pressable>
		))}
	</Modal>
);

const styles = StyleSheet.create({
	bar: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 14,
		paddingHorizontal: 20,
		paddingVertical: 14,
		borderBottomWidth: 1,
		borderBottomColor: '#e8eaed',
		backgroundColor: '#fff',
		flexWrap: 'wrap',
	},
	title: { fontSize: 21, fontWeight: '700', color: '#1a2027' },
	search: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
		// Pushes the search right and lets it absorb slack, so the title stays left and the
		// actions stay right — with minWidth 0 so it truncates rather than overflowing.
		flex: 1,
		minWidth: 160,
		marginLeft: 'auto',
		maxWidth: 420,
		borderWidth: 1,
		borderColor: '#e0e3e7',
		borderRadius: 8,
		paddingHorizontal: 12,
		paddingVertical: 8,
		backgroundColor: '#fbfcfd',
	},
	searchText: { flex: 1, minWidth: 0, color: '#90a4ae', fontSize: 14 },
	kbd: {
		borderWidth: 1,
		borderColor: '#e0e3e7',
		borderRadius: 4,
		paddingHorizontal: 5,
		paddingVertical: 1,
		backgroundColor: '#fff',
	},
	kbdText: { fontSize: 11, color: '#78909c', fontWeight: '600' },
	plus: {
		width: 36, height: 36, borderRadius: 18,
		backgroundColor: '#1976d2',
		alignItems: 'center', justifyContent: 'center',
	},
	iconButton: {
		width: 36, height: 36, borderRadius: 18,
		alignItems: 'center', justifyContent: 'center',
	},
	iconButtonBusy: { opacity: 0.6 },
	modalTitle: { fontSize: 18, fontWeight: '700', color: '#1a2027', marginBottom: 12 },
	option: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 14,
		paddingVertical: 12,
		paddingHorizontal: 4,
		borderBottomWidth: 1,
		borderBottomColor: '#f1f3f4',
	},
	optionIcon: {
		width: 42, height: 42, borderRadius: 21, backgroundColor: '#e3f2fd',
		alignItems: 'center', justifyContent: 'center',
	},
	optionText: { flex: 1, minWidth: 0 },
	optionLabel: { fontSize: 15, fontWeight: '600', color: '#263238' },
	optionDesc: { fontSize: 12, color: '#78909c', marginTop: 1 },
	avatar: {
		width: 34, height: 34, borderRadius: 17,
		backgroundColor: '#26a69a',
		alignItems: 'center', justifyContent: 'center',
	},
	avatarText: { color: '#fff', fontWeight: '700', fontSize: 12 },
});
